import React from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Avatar,
  Button
} from '@mui/material';
import { FormatQuote, CheckCircleOutline, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { scrollToFooterContact } from '../utils/scrollToFooter';

const APPLE_BG = '#f5f5f7';
const APPLE_BLUE = '#0071e3';

const WHY_CHOOSE_US = [
  'Manufacturing Top Quality Products',
  'Creating Unique Colours & Designs to bring out each team’s individuality',
  'Maximizing Athlete Performance',
  'Reaching the Global Market',
  'Exceeding Expectations',
  'Creating Innovative Product'
];

const applePillPrimary = {
  borderRadius: '980px',
  px: 2.5,
  py: 1.25,
  fontSize: '13px',
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: 'none',
  bgcolor: APPLE_BLUE,
  color: '#fff',
  boxShadow: 'none',
  width: 'auto',
  minHeight: 'unset',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': { bgcolor: '#0077ed', boxShadow: 'none' }
};

const applePillOutline = {
  borderRadius: '980px',
  px: 2.5,
  py: 1.25,
  fontSize: '13px',
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: 'none',
  width: 'auto',
  minHeight: 'unset',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  color: APPLE_BLUE,
  borderColor: APPLE_BLUE,
  borderWidth: 1,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(0,113,227,0.06)',
    borderColor: APPLE_BLUE,
    borderWidth: 1
  }
};

const sectionHeadingSx = {
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: '#1d1d1f',
  fontSize: { xs: '2rem', md: '2.75rem' },
  lineHeight: 1.08
};

const bodyTextSx = {
  color: '#6e6e73',
  lineHeight: 1.5,
  fontSize: { xs: '1.125rem', md: '1.25rem' }
};

function SplitSection({ imageSrc, imageAlt, imageSide = 'right', bgcolor = 'transparent', children }) {
  const imageFirst = imageSide === 'left';

  return (
    <Box
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        width: '100%',
        minHeight: { md: 'min(85vh, 820px)' },
        alignItems: 'stretch',
        bgcolor
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5, md: 6, lg: 12 },
          py: { xs: 7, md: 8 },
          order: { xs: 2, md: imageFirst ? 2 : 1 }
        }}
      >
        <Box sx={{ maxWidth: 560, width: '100%' }}>{children}</Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 400, md: '100%' },
          width: '100%',
          overflow: 'hidden',
          order: { xs: 1, md: imageFirst ? 1 : 2 }
        }}
      >
        <Box
          component="img"
          src={imageSrc}
          alt={imageAlt}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block'
          }}
        />
      </Box>
    </Box>
  );
}

const About = () => {
  return (
    <Box sx={{ bgcolor: APPLE_BG, width: '100%', overflowX: 'hidden' }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 'auto', md: '100vh' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'block' },
          overflow: 'hidden',
          bgcolor: { xs: '#1d1d1f', md: '#0a0a0a' }
        }}
      >
        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            inset: { md: 0 },
            width: '100%',
            height: { xs: 'min(52vh, 440px)', md: '100%' },
            minHeight: { xs: 300, md: '100%' },
            flexShrink: 0,
            zIndex: 0
          }}
        >
          <Box
            component="img"
            src="/assets/home/JS68201971.avif"
            alt=""
            aria-hidden
            sx={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              objectPosition: { xs: 'center 30%', md: 'center' }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: {
                xs: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 70%, #1d1d1f 100%)',
                md: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.45))'
              },
              zIndex: 1
            }}
          />
        </Box>

        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            inset: { md: 0 },
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: { xs: 5, sm: 6, md: 12 },
            px: { xs: 2.5, md: 0 },
            mt: { xs: -6, md: 0 }
          }}
        >
          <Container maxWidth="md">
            <Stack spacing={{ xs: 3, md: 4 }} alignItems="center">
              <Typography
                variant="overline"
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  fontSize: '0.875rem'
                }}
              >
                Company
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '2.25rem', sm: '2.75rem', md: '4rem' },
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em',
                  color: '#fff',
                  textShadow: '0 2px 24px rgba(0,0,0,0.35)'
                }}
              >
                Welcome to Zaror.
              </Typography>
              <Typography
                sx={{
                  maxWidth: 720,
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: { xs: '1.125rem', md: '1.375rem' },
                  lineHeight: 1.45
                }}
              >
                Why settle for off-the-shelf uniforms when you can have custom-made teamwear
                that sets your team apart?
              </Typography>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  component={Link}
                  to="/football"
                  variant="contained"
                  disableElevation
                  endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
                  sx={{
                    ...applePillPrimary,
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  Start designing
                </Button>
                <Button
                  variant="outlined"
                  onClick={scrollToFooterContact}
                  sx={{
                    ...applePillOutline,
                    borderColor: 'rgba(255,255,255,0.85)',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: 'rgba(255,255,255,0.12)'
                    }
                  }}
                >
                  Talk to our team
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>

      <Box component="section" sx={{ width: '100%', bgcolor: APPLE_BG }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            width: '100%',
            minHeight: { md: 'min(70vh, 640px)' },
            alignItems: 'stretch'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 3, sm: 5, md: 6, lg: 12 },
              py: { xs: 7, md: 8 },
              order: { xs: 2, md: 1 }
            }}
          >
            <Stack spacing={3} sx={{ maxWidth: 520, width: '100%' }}>
              <FormatQuote sx={{ fontSize: 48, color: APPLE_BLUE }} />
              <Typography
                sx={{
                  color: '#1d1d1f',
                  fontWeight: 500,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  lineHeight: 1.35,
                  letterSpacing: '-0.01em'
                }}
              >
                Attitude, confidence, possibility — I want people to wear Zaror and feel
                those feelings.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src="/assets/home/overview-team.png"
                  alt="Tommy Rivers"
                  sx={{ width: 64, height: 64 }}
                />
                <Stack spacing={0}>
                  <Typography sx={{ fontWeight: 600, color: '#1d1d1f' }}>
                    Tommy Rivers
                  </Typography>
                  <Typography sx={{ color: '#6e6e73', fontSize: '0.9375rem' }}>
                    Designer
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 360, md: '100%' },
              overflow: 'hidden',
              order: { xs: 1, md: 2 }
            }}
          >
            <Box
              component="img"
              src="/assets/home/overview-action.jpg"
              alt="Zaror team collaboration"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      </Box>

      <SplitSection
        imageSrc="/assets/home/hero.jpg"
        imageAlt="Zaror bespoke kits"
        imageSide="right"
        bgcolor="#fff"
      >
        <Typography
          variant="overline"
          sx={{
            color: APPLE_BLUE,
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: '0.875rem'
          }}
        >
          Our mission
        </Typography>
        <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
          Premium bespoke sports clothing
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Empowering teams to perform at their best, our mission at Zaror is to craft premium
          bespoke sports clothing that seamlessly blends innovation, style, and comfort. With
          meticulous attention to detail and a commitment to excellence, we strive to elevate
          the athletic experience.
        </Typography>
        <Typography sx={bodyTextSx}>
          Our dedication to quality craftsmanship and personalized service ensures that every
          piece of clothing we create embodies the spirit of individuality, enabling athletes
          to push boundaries, break records, and conquer challenges with confidence.
        </Typography>
      </SplitSection>

      <SplitSection
        imageSrc="/assets/home/overview-action.jpg"
        imageAlt="Team collaboration"
        imageSide="left"
        bgcolor={APPLE_BG}
      >
          <Typography
            variant="overline"
            sx={{
              color: APPLE_BLUE,
              fontWeight: 600,
              letterSpacing: '0.08em',
              fontSize: '0.875rem'
            }}
          >
            Why choose us
          </Typography>
          <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
            Built for teams who expect more
          </Typography>
          <Stack spacing={1.75}>
            {WHY_CHOOSE_US.map((point, index) => (
              <Stack direction="row" spacing={1.5} alignItems="flex-start" key={index}>
                <CheckCircleOutline
                  sx={{ color: APPLE_BLUE, fontSize: 22, mt: 0.25, flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    color: '#1d1d1f',
                    fontSize: { xs: '1.0625rem', md: '1.125rem' },
                    lineHeight: 1.45
                  }}
                >
                  {point}
                </Typography>
              </Stack>
            ))}
          </Stack>
      </SplitSection>

      <SplitSection
        imageSrc="/assets/home/why-us.jpg"
        imageAlt="Zaror manufacturing quality"
        imageSide="right"
        bgcolor="#fff"
      >
        <Typography
          variant="overline"
          sx={{
            color: APPLE_BLUE,
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: '0.875rem'
          }}
        >
          Manufacturing
        </Typography>
        <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
          Top quality products
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Whether you’re a grassroots team, a collegiate powerhouse, or a professional club, we
          understand that every team has its own unique requirements and aspirations. That’s why
          we offer a comprehensive range of customization options, from design and colour
          selection to fabric choice and branding.
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 3 }}>
          We’re not just in the business of making clothing — we’re in the business of building
          relationships and fostering success. Through exceptional customer service, timely
          delivery, and unwavering support, we strive to be a trusted partner in your team’s
          journey to greatness.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
          <Button
            variant="contained"
            disableElevation
            endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
            sx={applePillPrimary}
            onClick={scrollToFooterContact}
          >
            Request a quote
          </Button>
          <Button component={Link} to="/football" variant="outlined" sx={applePillOutline}>
            Launch kit designer
          </Button>
        </Stack>
      </SplitSection>
    </Box>
  );
};

export default About;
